import { Paperclip } from "lucide-react";
import { type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import type { CommentFragment } from "@/lib/board";

import { ImageThumb } from "./ImageThumb";

export const LINK_CLASS =
  "text-clickup-purple underline underline-offset-2 break-words";

// Wrap bare URLs in plain text as clickable links (ClickUp autolinks them too).
export function autolink(text: string, keyBase: string): ReactNode[] {
  return text.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={`${keyBase}-${i}`}
        href={part}
        target="_blank"
        rel="noreferrer"
        className={LINK_CLASS}
      >
        {part}
      </a>
    ) : (
      part
    ),
  );
}

// Render a ClickUp comment from its rich fragments (comment-formatting spec):
// links, task/user mentions, inline code/bold/italic. Falls back to plain text.
export function CommentBody({
  fragments,
  fallback,
}: {
  fragments: CommentFragment[];
  fallback: string;
}) {
  if (fragments.length === 0) {
    return (
      <span className="whitespace-pre-wrap break-words">
        {autolink(fallback, "f")}
      </span>
    );
  }
  return (
    <span className="whitespace-pre-wrap break-words">
      {fragments.map((f, i) => {
        // Image/file attachments: ClickUp sends an `attachment` block whose
        // fragment text is just the filename — render the image (or a file chip)
        // instead of the bare name.
        if (f.attachment) {
          const a = f.attachment;
          const href = a.url ?? a.thumbnail ?? undefined;
          const isImage =
            a.mimetype?.startsWith("image/") ||
            /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i.test(a.title ?? "") ||
            ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "avif"].includes(
              (a.extension ?? "").toLowerCase(),
            );
          if (isImage && (a.thumbnail || a.url)) {
            return (
              <ImageThumb
                key={i}
                thumbSrc={a.thumbnail ?? a.url ?? ""}
                fullSrc={a.url ?? a.thumbnail ?? ""}
                alt={a.title ?? "Attachment"}
                containerClassName="mt-1.5"
              />
            );
          }
          return (
            <a
              key={i}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="my-1 inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs text-foreground no-underline hover:bg-muted"
            >
              <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">{a.title ?? "Attachment"}</span>
            </a>
          );
        }
        if (f.type === "task_mention") {
          return (
            <a
              key={i}
              href={`https://app.clickup.com/t/${f.text}`}
              target="_blank"
              rel="noreferrer"
              className="mx-0.5 rounded bg-clickup-purple/15 px-1.5 py-0.5 text-xs font-medium text-clickup-purple no-underline"
            >
              ↗ {f.text}
            </a>
          );
        }
        if (f.type === "user_mention") {
          return (
            <span key={i} className="font-medium text-clickup-purple">
              @{f.text.replace(/^@/, "")}
            </span>
          );
        }
        if (f.link) {
          return (
            <a
              key={i}
              href={f.link}
              target="_blank"
              rel="noreferrer"
              className={LINK_CLASS}
            >
              {f.text || f.link}
            </a>
          );
        }
        if (f.code) {
          return (
            <code key={i} className="rounded bg-muted px-1 py-0.5 text-xs">
              {f.text}
            </code>
          );
        }
        if (f.bold || f.italic) {
          return (
            <span
              key={i}
              className={`${f.bold ? "font-semibold " : ""}${f.italic ? "italic" : ""}`.trim()}
            >
              {autolink(f.text, String(i))}
            </span>
          );
        }
        return <span key={i}>{autolink(f.text, String(i))}</span>;
      })}
    </span>
  );
}

// Flatten a React node tree to its text — used to detect ClickUp's multi-line
// "bookmark" links (title + domain + url crammed into one link's text).
function textOf(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (node && typeof node === "object" && "props" in node) {
    return textOf(
      (node as { props?: { children?: ReactNode } }).props?.children,
    );
  }
  return "";
}

// ClickUp "bookmark" embeds come through as a link whose text spans BLANK lines
// (title / domain / url). CommonMark forbids blank lines in link text, so it
// won't parse as a link at all — collapse each to a clean single-line link.
function collapseBookmarkLinks(md: string): string {
  return md.replace(
    /\[\s*([\s\S]*?)\s*\]\((https?:\/\/[^\s)]+)\)/g,
    (_m, text: string, url: string) => {
      const label =
        text
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean)[0] ?? "";
      return `[${label || url}](${url})`;
    },
  );
}

// ClickUp's rich description, as markdown — preserves the numbered lists,
// links (opened in a new tab), and code blocks that plain text drops.
export function Description({ markdown }: { markdown: string }) {
  const cleaned = collapseBookmarkLinks(markdown);
  return (
    <div className="text-sm leading-relaxed text-foreground [&_a]:text-clickup-purple [&_a]:underline [&_a]:underline-offset-2 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_h1]:mb-1 [&_h1]:mt-3 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:mb-1 [&_h2]:mt-3 [&_h2]:font-semibold [&_li]:my-0.5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-2 [&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&>:first-child]:mt-0 [&>:last-child]:mb-0">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node: _node, children, ...props }) => {
            // Bookmark embeds arrive as a link whose text spans several lines
            // (title / domain / url). Show just the first line — the title.
            const flat = textOf(children);
            const label = flat.includes("\n")
              ? (flat
                  .split("\n")
                  .map((s) => s.trim())
                  .filter(Boolean)[0] ?? children)
              : children;
            return (
              <a {...props} target="_blank" rel="noreferrer">
                {label}
              </a>
            );
          },
          // Inline description images → clickable thumbnail + lightbox.
          img: ({ node: _node, src, alt }) => {
            const s = typeof src === "string" ? src : "";
            return s ? (
              <ImageThumb
                thumbSrc={s}
                fullSrc={s}
                alt={typeof alt === "string" ? alt : ""}
                containerClassName="my-2"
              />
            ) : null;
          },
        }}
      >
        {cleaned}
      </ReactMarkdown>
    </div>
  );
}
